import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { first, map, retry, switchMap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  AnomalyReport,
  ContractorScore,
  HelpResponse,
  InsightsResponse,
  MapTemplateResponse,
  PeriodAnomalyReport,
  PeriodAnomalyReportDetail,
  PeriodAnomalyReportListResponse,
  PeriodReport,
  QueryResultResponse,
  QueryStartResponse,
  ValidationReport,
} from '../models/ai.model';

const AI_TIMEOUT_MS = 30_000;
const AI_MAX_RETRIES = 2;
const AI_POLL_INTERVAL_MS = 3_000;
const AI_POLL_MAX_MS = 180_000; // ~3 min overall guard

@Injectable({ providedIn: 'root' })
export class AiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.ai.endpoint;

  validateUpload(
    batchId: string,
    assetIds: number[],
    rows: { rowNumber: number; itemcode: string; weight_t: number; service_date: string }[]
  ): Observable<ValidationReport> {
    return this.http
      .post<ValidationReport>(`${this.baseUrl}/validateupload`, { batchId, assetIds, rows })
      .pipe(this.withTimeoutAndRetry());
  }

  checkAnomalies(
    period: string,
    contractorId: number | undefined,
    assetId: number,
    rows: {
      rowNumber: number;
      itemcode: string;
      weight_t: number;
      weight_kg: number;
      units_collected: number;
      service_date: string;
    }[]
  ): Observable<AnomalyReport> {
    const payload: Record<string, unknown> = { period, assetId, rows };
    if (contractorId != null) payload['contractorId'] = contractorId;
    return this.http
      .post<AnomalyReport>(`${this.baseUrl}/anomalycheck`, payload)
      .pipe(this.withTimeoutAndRetry());
  }

  /**
   * Starts an async AI query (POST /ai/query → 202 { queryId }), then polls
   * GET /ai/query/{queryId} every 3s until the status is terminal. Emits the
   * completed result exactly once, throws on a `failed` status, or times out
   * after ~3 minutes (RxJS TimeoutError).
   */
  queryData(question: string): Observable<QueryResultResponse> {
    return this.http
      .post<QueryStartResponse>(`${this.baseUrl}/query`, { question })
      .pipe(
        this.withTimeoutAndRetry(),
        switchMap(({ queryId }) =>
          timer(0, AI_POLL_INTERVAL_MS).pipe(
            switchMap(() =>
              this.http
                .get<QueryResultResponse>(`${this.baseUrl}/query/${queryId}`)
                .pipe(this.withTimeoutAndRetry())
            ),
            first((r) => r.status === 'completed' || r.status === 'failed'),
            timeout({ first: AI_POLL_MAX_MS }),
            map((r) => {
              if (r.status === 'failed') {
                throw new Error(r.errorMessage ?? 'Query failed');
              }
              return r;
            })
          )
        )
      );
  }

  generatePeriodReport(
    period: string,
    contractorId: number,
    assetId: number
  ): Observable<PeriodReport> {
    return this.http
      .post<PeriodReport>(`${this.baseUrl}/periodreport`, { period, contractorId, assetId })
      .pipe(this.withTimeoutAndRetry());
  }

  mapTemplate(
    headers: string[],
    sampleRows: string[][],
    contractorId: number
  ): Observable<MapTemplateResponse> {
    return this.http
      .post<MapTemplateResponse>(`${this.baseUrl}/maptemplate`, { headers, sampleRows, contractorId })
      .pipe(this.withTimeoutAndRetry());
  }

  getContractorScore(contractorId: number): Observable<ContractorScore> {
    return this.http
      .get<ContractorScore>(`${this.baseUrl}/contractorscore/${contractorId}`)
      .pipe(this.withTimeoutAndRetry());
  }

  getInsights(): Observable<InsightsResponse> {
    return this.http
      .get<InsightsResponse>(`${this.baseUrl}/insights`)
      .pipe(this.withTimeoutAndRetry());
  }

  getHelp(context: string, route: string, entity: string): Observable<HelpResponse> {
    return this.http
      .post<HelpResponse>(`${this.baseUrl}/help`, { context, route, entity })
      .pipe(this.withTimeoutAndRetry());
  }

  checkAnomaliesByPeriod(dateFrom: string, dateTo: string): Observable<PeriodAnomalyReport> {
    return this.http
      .post<PeriodAnomalyReport>(`${this.baseUrl}/anomalycheckperiod`, { date_from: dateFrom, date_to: dateTo })
      .pipe(this.withTimeoutAndRetry());
  }

  getPeriodAnomalyReports(): Observable<PeriodAnomalyReportListResponse> {
    return this.http
      .get<PeriodAnomalyReportListResponse>(`${this.baseUrl}/periodanomalyreports`)
      .pipe(this.withTimeoutAndRetry());
  }

  getPeriodAnomalyReportById(id: string): Observable<PeriodAnomalyReportDetail> {
    return this.http
      .get<PeriodAnomalyReportDetail>(`${this.baseUrl}/periodanomalyreports/${id}`)
      .pipe(this.withTimeoutAndRetry());
  }

  private withTimeoutAndRetry<T>(): (source: Observable<T>) => Observable<T> {
    return (source) =>
      source.pipe(
        timeout({ each: AI_TIMEOUT_MS }),
        retry({
          count: AI_MAX_RETRIES,
          delay: (err: unknown) => {
            if (err instanceof HttpErrorResponse && err.status >= 500) {
              return timer(1000);
            }
            return throwError(() => err);
          },
        })
      );
  }
}
